import React from "react";
import { Icon } from "@iconify/react";
import closeFill from "@iconify/icons-eva/close-fill";
import { Stack, Drawer, Divider, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getTextDirection } from "../../i18n";
import FormDirectionScope from "../../theme/FormDirectionScope";

/** @returns {Array<{ value: string, label: string }>} */
export function getSortByOptions(t) {
  return [
    { value: "featured", label: t("drawer.sortBy.featured") },
    { value: "newest", label: t("drawer.sortBy.newest") },
    { value: "priceDesc", label: t("drawer.sortBy.priceDesc") },
    { value: "priceAsc", label: t("drawer.sortBy.priceAsc") },
  ];
}

/**
 * @deprecated Use `getSortByOptions(t)` for translated labels.
 * English-only fallbacks (e.g. legacy call sites without `t`).
 */
export const SORT_BY_OPTIONS = getSortByOptions((key) => {
  const m = {
    "drawer.sortBy.featured": "Featured",
    "drawer.sortBy.newest": "Newest",
    "drawer.sortBy.priceDesc": "Price: high to low",
    "drawer.sortBy.priceAsc": "Price: low to high",
  };
  return m[key] ?? key;
});

export default function CustomDrawer({
  isOpenDrawer,
  onCloseDrawer,
  componentToPassDown,
  pageTitle,
  pageTitleKey,
  pageTitleValues,
  drawerClassName = "",
  isActionDisabled = false,
}) {
  const { t, i18n } = useTranslation();
  // Shell MUI theme stays LTR; anchor by content language so Urdu opens from the left (RTL pattern).
  const contentDir = getTextDirection(i18n.resolvedLanguage || i18n.language);
  const anchor = contentDir === "rtl" ? "left" : "right";
  const resolvedTitle = pageTitleKey
    ? t(pageTitleKey, pageTitleValues)
    : pageTitle;

  return (
    <>
      <Drawer
        className={`forms-drawer${drawerClassName ? ` ${drawerClassName}` : ""}`}
        anchor={anchor}
        open={isOpenDrawer}
        onClose={isActionDisabled ? undefined : onCloseDrawer}
        disableEscapeKeyDown={isActionDisabled}
        PaperProps={{
          sx: { border: "none", overflow: "hidden" },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 2.25 }}
        >
          <Typography
            className="drawer-title"
            variant="h6"
            sx={{ marginInlineStart: 1 }}
          >
            {resolvedTitle}
          </Typography>
          <IconButton
            className="drawer-cross-icon"
            onClick={isActionDisabled ? undefined : onCloseDrawer}
            disabled={isActionDisabled}
            aria-label={t("common.close")}
          >
            <Icon icon={closeFill} width={20} height={20} />
          </IconButton>
        </Stack>
        <Divider
          className="sidebar-divider"
          sx={{
            background: "#e2e8f0 !important",
            border: "none !important",
            height: "1px !important",
            margin: 0,
            opacity: 1,
            ".dark &": {
              background: "rgba(255, 255, 255, 0.12) !important",
            },
          }}
        />
        <div className="forms-drawer-height">
          <FormDirectionScope>{componentToPassDown}</FormDirectionScope>
        </div>
      </Drawer>
    </>
  );
}
